import type { ReactNode } from 'react';

import { Paper } from '@highlight-cp/core-ui';

import classes from './setup-step-card.module.css';

export interface SetupStepCardProps {
  /** The step's content. */
  children: ReactNode;
}

/**
 * ### SetupStepCard
 *
 * @description
 * The surface a project-setup step's content sits on — wrap a whole step, or use it as the panel
 * column of a `SidebarTabs` row. It owns the step's padding, so panels inside add none. Its fill and
 * shadow come from the stylesheet, following what it stands on: lifted off the paper inside a
 * `SidebarTabs` row, flat on the page without one.
 *
 * @example
 * ```tsx
 * <SetupStepCard>
 *   <TabsPanel value="products">…</TabsPanel>
 * </SetupStepCard>
 * ```
 *
 * @param props - {@link SetupStepCardProps}
 * @returns React component
 */
export const SetupStepCard = ({ children }: SetupStepCardProps) => (
  // `Paper`, not `Card`: no sections, and a `Card` would clip the sticky ControlBar/RightSidebarCard.
  // `withBorder` draws the hairline the design puts around every step card, and what a step's own
  // full-width dividers read as a continuation of. It carries the card's edge on its own — no shadow.
  <Paper className={classes.root} p="lg" radius="md" withBorder>
    {children}
  </Paper>
);
